import { MakeApiCallAsync } from './ApiHelpers';
import Config from './Config';
import { getProductVariations } from './ProductVariationsApi';

const round = amount => Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
const sameId = (left, right) => String(left || '').toLowerCase() === String(right || '').toLowerCase();
const decode = response => {
    const data = response?.data?.data;
    if (data == null) throw new Error('Unable to load current cart prices. Please try again.');
    return typeof data === 'string' ? JSON.parse(data) : data;
};

// Older APIs return a base-product quote even when a variant ID was requested.
// Reconcile the display with fresh product metadata, but never use that quote for payment.
export async function getCartPricing(cartItems) {
    if (!cartItems.length) return { productsData: [], cartSubTotal: 0, shippingSubTotal: 0, orderTotal: 0, variantPricingVerified: true };
    const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
    const result = decode(await MakeApiCallAsync(Config.END_POINT_NAMES.GET_CUSTOMER_CART_ITEMS, Config.COMMON_CONTROLLER_SUB_URL,
        { requestParameters: { cartJsonData: JSON.stringify(cartItems), recordValueJson: '[]' } }, headers, 'POST', true));
    if (!Array.isArray(result?.productsData)) throw new Error('Unable to load current cart prices. Please try again.');
    let variantPricingVerified = true;
    const productsData = await Promise.all(result.productsData.map(async product => {
        const selection = cartItems.find(item => sameId(item.ProductId, product.ProductId));
        if (!selection?.SelectedVariantId || sameId(selection.SelectedVariantId, product.SelectedVariantId)) return product;
        variantPricingVerified = false;
        const details = decode(await MakeApiCallAsync(Config.END_POINT_NAMES.GET_PRODUCT_DETAIL, null,
            { requestParameters: { ProductId: selection.ProductId, recordValueJson: '[]' } }, headers, 'POST', true));
        const detail = details?.[0];
        const variant = (await getProductVariations(selection.ProductId)).variations.find(item => sameId(item.id, selection.SelectedVariantId));
        if (!variant || variant.price == null || (variant.stock != null && variant.stock < selection.Quantity))
            throw new Error('The selected variant is unavailable. Please choose another variant.');
        const basePrice = Number(detail.Price);
        const quotedPrice = Number(product.Price);
        if (!Number.isFinite(basePrice) || !Number.isFinite(quotedPrice)) throw new Error('Unable to verify the selected variant price.');
        const price = round(variant.price + quotedPrice - basePrice);
        return { ...product, Price: price, DiscountedPrice: 0, DiscountId: null, IsDiscountCalculated: false,
            Quantity: Number(selection.Quantity), ItemSubTotal: round(price * selection.Quantity),
            SelectedVariantId: variant.id, SelectedVariantName: variant.name, SelectedVariantStock: variant.stock,
            SelectedVariantDescription: variant.rows.map(row => `${row.name}: ${row.values.join(', ')}`).join('; ') };
    }));
    for (const selection of cartItems) {
        if (!productsData.some(product => sameId(product.ProductId, selection.ProductId))) throw new Error('A cart item is unavailable. Please refresh your cart.');
    }
    const cartSubTotal = round(productsData.reduce((total, item) => total + Number(item.DiscountedPrice > 0 ? item.DiscountedPrice : item.Price) * Number(item.Quantity), 0));
    const shippingSubTotal = round(productsData.reduce((total, item) => total + Number(item.ShippingCharges || 0) * Number(item.Quantity), 0));
    return { ...result, productsData, cartSubTotal, shippingSubTotal, orderTotal: round(cartSubTotal + shippingSubTotal), variantPricingVerified };
}

export async function verifyCartForCheckout(cartItems) {
    const quote = await getCartPricing(cartItems);
    if (!quote.variantPricingVerified) throw new Error('The selected variant price could not be confirmed for checkout. Please try again later.');
    return quote;
}
